// ⛔ Il denaro non è mai un numero con la virgola: si tiene in CENTESIMI interi.
// Un `number` decimale in JavaScript perde i centesimi in silenzio (0.1 + 0.2),
// e qui i centesimi sono quelli che la segreteria deve quadrare a fine serata.
import { z } from 'zod';
/** Importo in centesimi: intero, può essere negativo (storni, scarichi). */
export const amountCentsSchema = z.number().int();
/** Importo in centesimi non negativo, per ciò che non può essere un rimborso. */
export const positiveAmountCentsSchema = z.number().int().positive();
/** 1234 → "12,34 €" — la formattazione italiana, in un posto solo. */
export function formatCents(cents) {
    return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
    }).format(cents / 100);
}
/**
 * "12,34" | "12.34" | "12" → 1234. Torna `null` su ciò che non è un importo,
 * invece di `NaN`: un NaN si propaga e si scopre tre schermate più in là.
 */
export function parseCents(input) {
    const normalized = input.trim().replace(/\s/g, '').replace(',', '.');
    if (!/^-?\d+(\.\d{1,2})?$/.test(normalized))
        return null;
    return Math.round(Number(normalized) * 100);
}
/**
 * Divide un importo fra N quote SENZA perdere centesimi: il resto della
 * divisione si distribuisce sulle prime quote, una unità per volta.
 *
 * ⚖️ `Math.round(totale / n)` per ciascuno non torna: 1000 in tre dà tre volte
 * 333 e un centesimo sparisce, oppure tre volte 334 e ne compare uno dal
 * nulla. Qui la somma delle quote è **sempre** il totale, ed è la proprietà
 * che la cassa deve poter dare per scontata a fine serata.
 *
 * ⛔ Con `quote <= 0` torna un elenco vuoto: dividere fra nessuno non è zero a
 * testa, è una domanda senza risposta, e chi chiama deve vederlo.
 */
export function ripartisciCents(totale, quote) {
    if (!Number.isInteger(totale) || !Number.isInteger(quote) || quote <= 0)
        return [];
    const segno = totale < 0 ? -1 : 1;
    const assoluto = Math.abs(totale);
    const base = Math.floor(assoluto / quote);
    const resto = assoluto - base * quote;
    return Array.from({ length: quote }, (_, i) => segno * (base + (i < resto ? 1 : 0)));
}
/**
 * 🚨 LA CHIAVE DI IDEMPOTENZA, e sta fra il DENARO perché è lì che serve.
 *
 * ⚖️ Una richiesta ripetuta — un dito che scivola, una rete che rimbalza, due
 * schede aperte — è un fastidio quasi ovunque e un **danno** qui: una ricarica
 * in più è denaro regalato, un incasso in più è denaro chiesto due volte. ⇒ Ogni
 * gesto che tocca il denaro ne porta una, e a rifiutare il secondo tentativo è
 * il database, non l'attenzione di chi guarda lo schermo.
 *
 * 🔀 **Chi la genera cambia, e le due forme convivono di proposito:**
 * · nel **borsellino** nasce con la finestra — due ricariche allo stesso socio
 *   sono due gesti legittimi, e una chiave deterministica le farebbe
 *   sovrascrivere l'una con l'altra;
 * · in **cassa** è deterministica (`chiaveIncasso`) — «Mario paga 8,00 € in
 *   contanti per questa partita» è un fatto, e ripeterlo da un altro computer è
 *   sempre lo stesso fatto.
 * 📌 Sono opposte perché rispondono a due domande diverse: «è lo stesso GESTO?»
 *    e «è lo stesso FATTO?».
 */
export const chiaveIdempotenzaSchema = z
    .string()
    .trim()
    .min(8, 'La chiave di idempotenza è troppo corta')
    .max(200);
//# sourceMappingURL=money.js.map